/**
 * PATH TO GO — orchestrator.
 *
 * `buildPathToGoState(site, sed, reports, intent, bandConfig?, composite?, bindingConstraints?)`
 * assembles the entire wizard state from the existing deterministic engines.
 * It REUSES — never re-implements — the verdict, composite, coverage, SED-gap,
 * intent-recommender, preflight, and readiness modules.
 *
 * CRITICAL HONESTY RULE: when the verdict's data is INADEQUATE
 * (`verdict.dataAdequate === false` — no source analyses, unresolved
 * high-severity conflicts, or coverageRatio < MIN_PILLAR_COVERAGE), we DO NOT
 * present a red NO-GO. A C confidence tag remains a visible provenance caveat
 * but does not, by itself, withhold the verdict. The state
 * marks the band WITHHELD while keeping Analyze available so screening work can
 * improve the record; only a final positive Decision remains gated. This
 * mirrors `evaluateSiteDataAdequacy`'s policy: a sparse site (coverage below
 * MIN_PILLAR_COVERAGE) is withheld rather than surfaced as a verdict. (Part C
 * SP4 made the composite aggregate HARD_MISSING, so a sparse site is no longer a
 * FALSE no-go in the score itself — but the coverage gate still withholds the
 * band because too few measured pillars cannot stand behind a recommendation.)
 *
 * Pure + STATELESS — no persistence, no store/Firestore reads. Recomputes from
 * the passed inputs every call. Same inputs → same output.
 */
import { deriveIntentForSelectedAssessment } from '../generationImpact/selectedAssessmentIntent';
import type { ReportTypeId } from '../../config/reportTypes';
import type { SiteEntity } from '../../types/site';
import type { SiteRagReport, ProjectIntent } from '../../types/siteRag';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { SiteComposite } from '../../types/portfolioComposite';
import type {
  DecisionAnalysisDomain,
  DecisionAnalysisInputEnvelopeV1,
} from '../../types/decisionAnalysis';
import { getRequiredSedSectionsFor } from '../../config/reportTypes';
import { computeSiteComposite } from '../portfolio/compositeScorer';
import {
  deriveSiteVerdict,
  type DerivedSiteVerdict,
} from '../portfolio/deriveSiteVerdict';
import type { DecisionBandConfig } from '../portfolio/decisionBands';
import type { DecisionGuideRollup, DerivedBand } from '../executiveDecisionRollup';
import {
  getDocumentChecklistAcross,
  matchUploadedDocsToChecklist,
  computeConfidenceFloor,
  docMatchesPatterns,
  type ChecklistItem,
  type RankedChecklistMatch,
} from '../../config/documentChecklists';
import { isLibraryIndexDoc } from '../knowledge/libraryIndexDocument';
import { docsEligibleForEvidence, siteContextForEligibleDocuments } from '../evidenceDocEligibility';
import type { SiteDocument } from '../../types/siteRag';
import {
  intentCriticalScope,
  type IntentMissingRow,
} from '../overview/computeOverviewFindingCards';
import {
  classifyRequirementDomain,
} from '../coverage/requirementDomainGroups';
import {
  computeRequirementStatusAcrossTypes,
  type RequirementStatus,
} from '../coverage/requirementStatus';
import {
  hasScreeningCoordinates,
  isFirstReportForSite,
  runPreflight,
  PREFLIGHT_BLOCKING_STATUSES,
} from '../preflightCheck';
import { computeEvidenceReadiness } from '../evidenceReadiness';
import { resolvePathToGoPlannedTypes } from './pathToGoPlannedTypes';
import {
  buildAnalysisPlan,
  latestUsableSavedReportOfType,
  planItemCompletesStage,
  type AnalysisPlanItem,
  type PathAction,
  type PathToGoAnalysisSnapshot,
  type ReportPlanItem,
  type ReportRuntimeLifecycle,
} from './reportPlan';
import { distanceToGo } from './distanceToGo';
import {
  stepsFromCoverageRows,
  stepsFromSedGaps,
  stepsFromIntentRecommendations,
  stepsFromReadinessRecommendations,
  filterReadinessStepsAgainstCoverage,
  stepsFromPreflight,
  stepsFromSiteBasisGaps,
  stepsFromAnalysisGaps,
  stepsFromGenerationGaps,
  stepsFromRoadmapGaps,
  stepsFromScreeningQuestions,
  rankSteps,
  type NextStep,
} from './nextStep';
import { siteHasBoundary } from '../siteBoundaryGeometry';
import { testFitWorkflowBlockMessage } from '../generationImpact/testFitWorkflow';
import { roadmapProgress } from '../audit/recommendationStatus';
import type { RecommendationGraph } from '../../types/recommendationGraph';
import { rankNextInputs, type NextInputRanking } from '../intentIntelligence/nextBestInput';
import {
  buildBlockers,
  countResolveBlockers,
  type Blocker,
  type MissingCriticalDoc,
} from './blockers';
import { capTierByFirmCapacity, type ReportVerdict } from '../reportVerdict';
import {
  assessGenerationImpactTestFitApplicability,
} from '../generationImpact/testFitApplicability';
import { resolveSiteStudyArea } from '../siteStudyArea';
import { resolvePerSourceAvailability } from '../powerAvailability/resolvePerSourceAvailability';
import { resolveSiteDecisionStage, type SiteDecisionStageStatus } from '../stage2/siteDecisionStage';
import { deriveStage2Decisions } from '../stage2/deriveStage2Decisions';
import type { Stage2DecisionSet } from '../stage2/stage2Types';
import { computeSedSectionContentHashesV2 } from '../decisionAnalysis/sedContentIdentity';

/** A domain analysis the wizard recommends running next. */
export interface RecommendedReport {
  type: ReportTypeId;
  /** True when preflight finds no blocking section for this type's SED needs. */
  runnable: boolean;
  /** True when preflight has at least one blocking section. */
  blocked: boolean;
  /** Number of blocking SED sections gating this report. */
  blockingSections: number;
}

/** Analysis-first name; the legacy report projection remains supported. */
export type RecommendedAnalysis = RecommendedReport;

export type PathToGoStageId =
  | 'diagnose'
  | 'intent'
  | 'evidence'
  | 'resolve'
  | 'generate'
  | 'decision';

export type PathToGoStageStatus = 'cleared' | 'open' | 'locked';

export interface PathToGoStage {
  id: PathToGoStageId;
  status: PathToGoStageStatus;
  /** Count of open items the user must address in this stage. */
  openCount: number;
  /** Present only when status === 'locked'. Human-readable reason in
   *  Coverage Matrix vocabulary — shown in the stage rail sub-text. */
  lockedReason?: string;
  /** Optional supplementary coverage label for the Intent stage.
   *  Shows the site-project-intent requirement status alongside
   *  the form-completion state, so users understand why the two differ. */
  coverageLabel?: string;
  /**
   * Intent answers the user recorded as UNKNOWN. They are answered, so they are
   * not in `openCount` and are never asked again, but the stage/phase is not
   * cleared while they remain: an unknown is an open follow-up, and a cleared
   * Define phase is part of the positive (GO/CONDITIONAL) decision gate.
   */
  followUpCount?: number;
}

export type PathToGoPhaseId = 'define' | 'build' | 'verify' | 'decide';

export interface PathToGoPhase {
  id: PathToGoPhaseId;
  status: PathToGoStageStatus;
  openCount: number;
  lockedReason?: string;
  /**
   * Intent answers the user recorded as UNKNOWN. They are answered, so they are
   * not in `openCount` and are never asked again, but the stage/phase is not
   * cleared while they remain: an unknown is an open follow-up, and a cleared
   * Define phase is part of the positive (GO/CONDITIONAL) decision gate.
   */
  followUpCount?: number;
}

export interface PathToGoState {
  /**
   * Strict live Decision Guide reconciliation supplied by the host. The
   * effective `verdict` below is projected from this rollup when present.
   */
  decisionRollup?: DecisionGuideRollup;
  verdict: DerivedSiteVerdict;
  distanceToGo: number;
  adequacyGate: { adequate: boolean; reason?: string; coverageRatio: number };
  /** True while a valid coordinate-backed site has no analysis record yet. */
  coordinateFirstBootstrap?: boolean;
  /** Location screening remains available when older reports already exist. */
  hasScreeningCoordinates?: boolean;
  requiredReports: ReportTypeId[];
  /** Analysis-first alias; always populated by `buildPathToGoState`. */
  requiredAnalyses?: ReportTypeId[];
  /** Canonical lifecycle plan. `recommendNext` below is its legacy projection. */
  reportPlan: ReportPlanItem[];
  /** Analysis-first alias over the same canonical plan entries. */
  analysisPlan?: AnalysisPlanItem[];
  recommendNext: RecommendedReport[];
  /** Analysis-first alias over the legacy recommendation projection. */
  recommendNextAnalyses?: RecommendedAnalysis[];
  rankedSteps: NextStep[];
  blockers: Blocker[];
  stages: PathToGoStage[];
  /** The current four-phase cockpit model. */
  phases: PathToGoPhase[];
  /** First-class action selected from the report/dependency plan, when present. */
  primaryPathAction?: PathAction;
  generationImpactApplicability: ReturnType<typeof assessGenerationImpactTestFitApplicability>;
  /**
   * OPTIONAL side-quests (engineering sim, generation test fit, financial
   * assumptions, BOM, roadmap execution). NEVER merged into `rankedSteps`,
   * NEVER counted in stage open-counts, NEVER gating GO — they strengthen the
   * case, they don't decide it. Signal-gated: empty for sites that don't
   * trigger a quest.
   */
  sideQuests: NextStep[];
  /** Decision stage read from the composite's per-stage checklist. */
  decisionStage?: SiteDecisionStageStatus;
  /**
   * The Stage 2 decision checklist — available once every required early
   * screening analysis exists. Optional so persisted snapshots and fixtures
   * that predate it stay valid.
   */
  stage2Decisions?: Stage2DecisionSet;
}

/** Resolve the analysis-first plan while accepting legacy state snapshots. */
export function getPathToGoAnalysisPlan(
  state: {
    analysisPlan?: readonly AnalysisPlanItem[];
    reportPlan?: readonly ReportPlanItem[];
  },
): readonly AnalysisPlanItem[] {
  // The legacy field remains canonical while both names coexist. This keeps
  // object-spread overrides and persisted state fixtures byte-compatible.
  return state.reportPlan ?? state.analysisPlan ?? [];
}

/** Resolve required analysis domains while accepting legacy state snapshots. */
export function getRequiredPathToGoAnalyses(
  state: {
    requiredAnalyses?: readonly ReportTypeId[];
    requiredReports?: readonly ReportTypeId[];
  },
): readonly ReportTypeId[] {
  return state.requiredReports ?? state.requiredAnalyses ?? [];
}

/**
 * Optional extra inputs for surfaces the core (site, sed, reports, intent)
 * tuple can't see. Absent → those quests are simply not produced (byte-stable
 * for existing call sites).
 */
export interface PathToGoExtras {
  /** The site's roadmap graph (store `recommendationGraphBySite[siteId]`). */
  recommendationGraph?: RecommendationGraph | null;
  /** Strict live system recommendation shared with the Decision Guide. */
  decisionRollup?: DecisionGuideRollup;
  /**
   * The authoritative SED is still being resolved. Interactive cockpit
   * surfaces use this to avoid presenting recommendations derived from a
   * transient `null` document as real blockers.
   */
  evidenceLoading?: boolean;
  /** Whether the Generation & Impact tab is visible in this build/host. */
  generationTabEnabled?: boolean;
  /** Whether simulation-backed Engineering Analysis is visible in this build. */
  simulationTabEnabled?: boolean;
  /** Explicit edit capability. False converts mutating CTAs to access requests. */
  canEdit?: boolean;
  /** Optional live job projection supplied by the host/store. */
  reportRuntimeByType?: Partial<Record<ReportTypeId, ReportRuntimeLifecycle>>;
  /** Analysis-first alias over the same legacy report-job projection. */
  analysisRuntimeByType?: Partial<Record<ReportTypeId, ReportRuntimeLifecycle>>;
  /** Settled generation/analysis outcomes. */
  reportOutcomes?: readonly import('./reportRuntime').ReportRuntimeOutcome[];
  analysisOutcomes?: readonly import('./reportRuntime').ReportRuntimeOutcome[];
  /**
   * Domains whose decision pointer carries `autoRebindExhaustedAt` — the
   * auto-rebind cap has given up and nothing will refresh them on its own.
   * Path to GO renders from the plan without the pointer, so it has to be told.
   */
  analysisAutoRefreshExhaustedByType?: Partial<Record<ReportTypeId, boolean>>;
  /**
   * Server-owned rollout gate. Semantic analyses may be displayed in shadow
   * and read modes, but may satisfy GO/CONDITIONAL only when this is true.
   * Omitted defaults fail closed to the legacy report-backed gate.
   */
  analysisGateEnabled?: boolean;
  /**
   * Loaded immutable semantic analyses and their persisted revision locators.
   * A report artifact is not required when one of these is current and ready.
   */
  analysisSnapshots?: readonly PathToGoAnalysisSnapshot[];
  /**
   * Live canonical inputs built by the decision-analysis identity boundary.
   * Path-to-GO evaluates these rather than reimplementing SED/intent/authority
   * currentness.
   */
  currentAnalysisInputsByDomain?: Partial<
    Record<DecisionAnalysisDomain, DecisionAnalysisInputEnvelopeV1>
  >;
  /**
   * The ranked DCT match per report type (`matchUploadedDocsToChecklistRanked`
   * output). When present, coverage-row gap computation sees uploaded
   * documents: Tier-3 status upgrades apply, and rows with a matching
   * upload are labelled VERIFY gaps instead of asking the user to upload
   * a document they already provided. Absent → prior behaviour (the sync
   * tag-pattern fallback still detects uploads for the copy switch).
   * SiteLandingPage supplies it from the page-level `useSiteCoverageMatch`
   * run that also feeds the Site Evidence tab; the dashboard queue, report
   * generation and the server decision router run without it. It changes step
   * copy, severity and rank only — never phases or the positive-decision gate,
   * which the server re-derives without it
   * (pathToGoOrchestrator.coverageMatch.test.ts).
   */
  coverageMatchByType?: ReadonlyMap<ReportTypeId, RankedChecklistMatch>;
  /**
   * Today as an ISO date for the Stage 2 lead-time rules. Tests pin it; hosts
   * omit it and the current date is used.
   */
  asOf?: string;
}

/**
 * The Stage 2 decision checklist for this site. Study-area resolution throws
 * on an invalid selection; that case reads as "no study area" here because
 * the Map tab already owns the repair prompt.
 */
function stage2DecisionsFor(
  site: SiteEntity,
  sed: SiteEvidenceDocument | null,
  reports: readonly SiteRagReport[],
  decisionStage: SiteDecisionStageStatus,
  asOf: string,
): Stage2DecisionSet {
  let studyAreaAcres: number | null = null;
  try {
    studyAreaAcres = resolveSiteStudyArea(site)?.scope.grossAreaAcres ?? null;
  } catch {
    studyAreaAcres = null;
  }
  return deriveStage2Decisions({
    site,
    sed,
    reports,
    stage: decisionStage,
    availability: resolvePerSourceAvailability({
      sed,
      promotedScenario: site.promotedGenerationScenarioSnapshot ?? null,
    }),
    studyAreaAcres,
    asOf,
    // The same section content identity report currency uses, so a Public
    // Sentiment refresh does not mark accepted decisions as resting on changed evidence.
    hashSedSections: computeSedSectionContentHashesV2,
  });
}

/* ── Helpers ────────────────────────────────────────────────────────── */

const COMPLETE_STATUSES: RequirementStatus[] = [
  'authored-strong',
  'strong',
  'authored',
  'adequate',
];

function reportTypesPresent(reports: readonly SiteRagReport[]): Set<ReportTypeId> {
  const present = new Set<ReportTypeId>();
  for (const r of reports) present.add((r.reportType ?? 'feasibility') as ReportTypeId);
  return present;
}

/**
 * Uploaded documents that plausibly cover `req` — union of (a) explicit
 * uploads targeted at this requirement (`intendedRequirementIds`), (b) the
 * ranked DCT matches when the host supplied them, and (c) the sync
 * tag-pattern predicate (`docMatchesPatterns` — the SAME predicate the
 * evidence-readiness recommendations use, so "readiness stopped asking"
 * and "coverage row says you uploaded this" can never disagree).
 * Deterministic order (docId), capped at 3 for the step copy.
 */
function matchedDocsForRequirement(
  req: ChecklistItem,
  documents: readonly SiteDocument[],
  matchByType: ReadonlyMap<ReportTypeId, RankedChecklistMatch> | undefined,
): { docId: string; fileName: string }[] {
  const byId = new Map<string, SiteDocument>();
  const liveDocs = documents.filter(
    (d) => !isLibraryIndexDoc(d) && (d as { deletedAt?: string | null }).deletedAt == null,
  );
  for (const d of liveDocs) byId.set(d.id, d);
  const matchedIds = new Set<string>();
  for (const d of liveDocs) {
    if ((d.intendedRequirementIds ?? []).includes(req.id)) matchedIds.add(d.id);
    else if (docMatchesPatterns(d, req.relevanceTagPattern)) matchedIds.add(d.id);
  }
  if (matchByType) {
    for (const m of matchByType.values()) {
      for (const r of m.matched.get(req.id) ?? []) {
        if (byId.has(r.docId)) matchedIds.add(r.docId);
      }
    }
  }
  return [...matchedIds]
    .sort((a, b) => a.localeCompare(b))
    .slice(0, 3)
    .map((id) => ({ docId: id, fileName: byId.get(id)?.fileName ?? id }));
}

/**
 * Intent-critical missing coverage rows — mirrors `computeIntentEvidenceCard`'s
 * composition (intentCriticalScope → getDocumentChecklistAcross → domain filter
 * → computeRequirementStatusAcrossTypes), but returns ONLY the missing rows.
 * Tier 1 (authored) + Tier 2 (SED-slot) statuses always resolve exactly; the
 * Tier-3 DCT-score upgrade applies when the host threads the async ranked
 * matcher's result via `matchByType` (PathToGoExtras.coverageMatchByType) —
 * absent, only Tiers 1+2 resolve (the prior behaviour). Each returned row
 * additionally carries `matchedDocs` so downstream copy can distinguish
 * "upload this" from "you uploaded this — extraction hasn't confirmed it".
 */
export function intentCriticalMissingRows(
  site: SiteEntity,
  sed: SiteEvidenceDocument | null,
  intent: ProjectIntent | undefined,
  matchByType?: ReadonlyMap<ReportTypeId, RankedChecklistMatch>,
  analysisTypes?: readonly ReportTypeId[],
): IntentMissingRow[] {
  if (!intent) return [];
  const { reportTypes: broadReportTypes, domains } = intentCriticalScope(intent);
  const reportTypes = analysisTypes ?? broadReportTypes;
  const requirements = getDocumentChecklistAcross([...reportTypes]).filter((r) =>
    domains.includes(classifyRequirementDomain(r)),
  );
  // Retained library history is not the source scope of current evidence.
  // Use the same curation rule as SED/report generation, including ai-only.
  const documents = docsEligibleForEvidence(site);
  const eligibleIds = new Set(documents.map(document => document.id));
  const authoredClaims = siteContextForEligibleDocuments(site.siteContext, eligibleIds)?.claims
    .filter(claim => claim.state !== 'mis-extracted');
  // A cached matcher can outlive a document toggle. It may describe current
  // documents, but cannot restore excluded history through the Tier-3 rung.
  const effectiveMatch: ReadonlyMap<string, RankedChecklistMatch> = new Map(
    [...(matchByType ?? new Map<ReportTypeId, RankedChecklistMatch>())].map(([type, match]) => [type, {
      ...match,
      matched: new Map([...match.matched].map(([id, matches]) => [id,
        matches.filter(row => eligibleIds.has(row.docId)),
      ])),
    }] as const),
  );
  const rows: IntentMissingRow[] = [];
  for (const req of requirements) {
    const status = computeRequirementStatusAcrossTypes({
      requirement: req,
      sed: sed ?? undefined,
      documents,
      authoredClaims,
      matchByType: effectiveMatch,
      projectIntent: intent,
      typedPreferences: site.typedPreferencesDefaults,
    });
    if (!COMPLETE_STATUSES.includes(status)) {
      const matchedDocs = matchedDocsForRequirement(req, documents, effectiveMatch as ReadonlyMap<ReportTypeId, RankedChecklistMatch>);
      rows.push({
        id: req.id,
        label: req.label,
        domain: classifyRequirementDomain(req),
        status,
        ...(matchedDocs.length > 0 ? { matchedDocs } : {}),
      });
    }
  }
  return rows;
}

/**
 * Missing CRITICAL checklist docs across the scope report types — via
 * `matchUploadedDocsToChecklist` + `computeConfidenceFloor` (the same
 * confidence-floor primitive the wizard uses). Only fires for sites in
 * docs-included mode where critical docs are a real gate.
 */
function missingCriticalDocs(
  site: SiteEntity,
  scopeTypes: readonly ReportTypeId[],
): MissingCriticalDoc[] {
  const docs = docsEligibleForEvidence(site);
  const out: MissingCriticalDoc[] = [];
  const seen = new Set<string>();
  for (const t of scopeTypes) {
    const match = matchUploadedDocsToChecklist(docs, t);
    const floor = computeConfidenceFloor(match);
    for (const item of floor.missingCriticalItems) {
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      out.push({ id: item.id, label: item.label, reportType: t });
    }
  }
  return out;
}

/**
 * Preflight a report type against the SED — which source sections are missing?
 * Reuses `runPreflight` on the type's `requiredSedSections`. A coordinate-only
 * first run uses bootstrap mode because that run collects public source data;
 * later runs keep strict missing-section visibility as disclosed improvements,
 * not analysis-launch gates.
 */
function preflightForType(
  sed: SiteEvidenceDocument | null,
  type: ReportTypeId,
  bootstrapMode: boolean,
): { runnable: boolean; blockingSections: number } {
  const required = getRequiredSedSectionsFor(type);
  const result = runPreflight(sed, required, { bootstrapMode, reportTypeId: type });
  return { runnable: result.canGenerate, blockingSections: result.redCount };
}

/* ── Stage assembly ─────────────────────────────────────────────────── */

function buildStages(args: {
  adequate: boolean;
  adequacyReason: string | null;
  intentMissingCount: number;
  intentRecordedUnknownCount: number;
  evidenceStepCount: number;
  blockers: readonly Blocker[];
  recommendNext: readonly RecommendedReport[];
  requiredSatisfied: boolean;
  isGo: boolean;
  intentCoverageStatus: string | null;
  intentComplete: boolean;
}): PathToGoStage[] {
  const {
    adequate,
    adequacyReason,
    intentMissingCount,
    intentRecordedUnknownCount,
    evidenceStepCount,
    blockers,
    recommendNext,
    requiredSatisfied,
    isGo,
    intentCoverageStatus,
    intentComplete,
  } = args;

  // Resolve-stage open items = the resolve-owned blockers the user must clear.
  // SINGLE SOURCE OF TRUTH: `countResolveBlockers` is the SAME predicate the
  // ResolveStage body filters on, so the rail badge and the panel list can never
  // diverge (previously the badge counted `missing-doc:` blockers the body
  // filtered out → "1" on the rail, "no conflicts" in the body).
  const resolveOpen = countResolveBlockers(blockers);
  const generateOpen = recommendNext.length;

  // Diagnose is always cleared once we've produced a state (it IS the diagnosis).
  const diagnose: PathToGoStage = { id: 'diagnose', status: 'cleared', openCount: 0 };

  const intent: PathToGoStage = {
    id: 'intent',
    status: intentMissingCount === 0 ? 'cleared' : 'open',
    openCount: intentMissingCount,
    ...(intentRecordedUnknownCount > 0 ? { followUpCount: intentRecordedUnknownCount } : {}),
    coverageLabel: intentCoverageStatus
      ? `Evidence: ${intentCoverageStatus.charAt(0).toUpperCase() + intentCoverageStatus.slice(1)}`
      : undefined,
  };

  const evidence: PathToGoStage = {
    id: 'evidence',
    status: evidenceStepCount === 0 ? 'cleared' : 'open',
    openCount: evidenceStepCount,
  };

  const resolve: PathToGoStage = {
    id: 'resolve',
    status: resolveOpen === 0 ? 'cleared' : 'open',
    openCount: resolveOpen,
  };

  // Compute a human-readable locked hint in Coverage Matrix language.
  // The adequacyReason from evaluateSiteDataAdequacy() already says WHY in
  // plain English; prefix it with a coverage-model phrase so it is consistent.
  const adequacyHint = adequacyReason
    ? adequacyReason.charAt(0).toUpperCase() + adequacyReason.slice(1)
    : 'Needs adequate coverage across all requirement domains';

  // Analysis remains available even when the decision band is withheld. Its
  // job is to improve the evidence record; locking it on inadequate evidence
  // created a circular workflow. Only the accountable Decision stage stays
  // locked until adequacy and the required current-analysis set are satisfied.
  const generate: PathToGoStage = {
    id: 'generate',
    status: generateOpen === 0 ? 'cleared' : 'open',
    openCount: generateOpen,
  };

  const decision: PathToGoStage = {
    id: 'decision',
    status: !adequate || !requiredSatisfied || !intentComplete
      ? 'locked'
      : isGo
        ? 'cleared'
        : 'open',
    openCount: adequate && requiredSatisfied && !isGo ? 1 : 0,
    lockedReason: !adequate
      ? adequacyHint
      : !requiredSatisfied
        ? 'Needs usable successful results for required analyses first'
        : !intentComplete
          ? intentMissingCount > 0
            ? 'Needs decision-defining intent confirmed first'
            : 'Intent recorded as unknown — its follow-up is still open'
        : undefined,
  };

  return [diagnose, intent, evidence, resolve, generate, decision];
}

/**
 * Screening definition is complete when each early question has an answer.
 * Unknown remains recorded for report caveats; evidence and report authority
 * independently govern any positive decision. It is not a known fact.
 */
export function isIntentDefinitionComplete(
  ranking: Pick<NextInputRanking, 'ranked' | 'recordedUnknown'>,
  applicabilityOpen: number,
): boolean {
  return ranking.ranked.length === 0
    && applicabilityOpen === 0;
}

function buildPhases(args: {
  adequate: boolean;
  adequacyReason: string | null;
  defineOpen: number;
  /** Intent recorded as unknown — not asks, but Define is not cleared. */
  defineUnknown: number;
  buildOpen: number;
  verifyOpen: number;
  requiredSatisfied: boolean;
  isGo: boolean;
}): PathToGoPhase[] {
  const {
    adequate,
    adequacyReason,
    defineOpen,
    defineUnknown,
    buildOpen,
    verifyOpen,
    requiredSatisfied,
    isGo,
  } = args;
  const adequacyHint = adequacyReason
    ? adequacyReason.charAt(0).toUpperCase() + adequacyReason.slice(1)
    : 'Needs adequate evidence coverage';
  // Recorded unknowns stay visible as follow-ups; report/evidence gates decide readiness.
  const prerequisitesOpen = defineOpen + buildOpen + verifyOpen;

  return [
    {
      id: 'define',
      status: defineOpen === 0 ? 'cleared' : 'open',
      openCount: defineOpen,
      ...(defineUnknown > 0 ? { followUpCount: defineUnknown } : {}),
    },
    {
      id: 'build',
      status: buildOpen === 0 ? 'cleared' : 'open',
      openCount: buildOpen,
    },
    {
      id: 'verify',
      status: verifyOpen === 0 ? 'cleared' : 'open',
      openCount: verifyOpen,
    },
    {
      id: 'decide',
      // Decide stays inspectable: users may record an early NO-GO or DEFER.
      // The decision form independently disables GO/CONDITIONAL until the
      // positive gate is satisfied.
      status:
        adequate && requiredSatisfied && prerequisitesOpen === 0 && isGo
          ? 'cleared'
          : 'open',
      openCount:
        adequate && requiredSatisfied && prerequisitesOpen === 0 && isGo
          ? 0
          : 1,
      lockedReason: !adequate
        ? adequacyHint
        : !requiredSatisfied || buildOpen > 0
          ? 'Complete the required analysis plan first'
          : defineOpen > 0
            ? 'Confirm the project definition first'
          : verifyOpen > 0
              ? 'Resolve verification items first'
              : undefined,
    },
  ];
}

/**
 * Build a minimal `ReportVerdict`-shaped carrier holding ONLY the firm-capacity
 * cap, computed off the live intent via the SAME `capTierByFirmCapacity`
 * mechanism `getDisplayVerdict` runs (consistency mandate: the cap and the
 * Path-to-GO blocker share one source). Returns `undefined` when no cap fires
 * (the common case — OR-mode / single-source / unconfigured / feasible). We feed
 * the cap on a synthetic 'green' baseline so the cap's `preCapTier` reflects "the
 * best tier the firm-capacity reality would still permit", matching the lift
 * target the blocker surfaces.
 */
function buildFirmCapacityVerdictDisplay(
  intent: ProjectIntent | undefined,
): ReportVerdict | undefined {
  if (!intent) return undefined;
  const { cap } = capTierByFirmCapacity('green', intent);
  if (!cap) return undefined;
  return { firmCapacityCap: cap } as ReportVerdict;
}

function rollupBandColor(
  band: DerivedBand,
): DerivedSiteVerdict['bandColor'] {
  switch (band) {
    case 'go':
      return 'green';
    case 'conditional':
      return 'amber';
    case 'conditional-high':
      return 'orange';
    case 'no-go':
      return 'red';
    default:
      return 'gray';
  }
}

/**
 * Carry the strict executive reconciliation onto every live Path surface while
 * retaining the base composite/provenance fields used by score explanations.
 * The rollup is monotonic-down and may withhold a recommendation for unresolved
 * decision-defining intent even when the underlying score data is adequate.
 */
function applyDecisionRollup(
  base: DerivedSiteVerdict,
  rollup: DecisionGuideRollup | undefined,
): DerivedSiteVerdict {
  if (!rollup) return base;
  return {
    ...base,
    band: rollup.band,
    bandLabel: rollup.label,
    bandColor: rollupBandColor(rollup.band),
    dataAdequate: rollup.adequacy.adequate,
    adequacyReason: rollup.adequacy.reason,
    coverageRatio: rollup.adequacy.coverageRatio,
  };
}

/* ── Public entry point ─────────────────────────────────────────────── */

/**
 * Build the full PATH TO GO state. Stateless + deterministic.
 *
 * @param composite optional precomputed `SiteComposite` — pass it to avoid a
 *                  recompute (the orchestrator otherwise runs the real
 *                  `computeSiteComposite` oracle). MUST be the genuine oracle
 *                  output for the verdict/distance to be exact.
 * @param bindingConstraints optional — the EXACT caps that fired in the
 *                  authoritative Go/No-Go rollup
 *                  (`buildExecutiveDecisionRollup().bindingConstraints`). Threaded
 *                  into the blocker layer so the Resolve stage lists every cap
 *                  binding the band. Last + optional so all existing call sites
 *                  (and tests) stay valid.
 */
export function buildPathToGoState(
  site: SiteEntity,
  sed: SiteEvidenceDocument | null,
  reports: SiteRagReport[],
  intent: ProjectIntent | undefined,
  bandConfig?: DecisionBandConfig | null,
  composite?: SiteComposite | null,
  bindingConstraints?: readonly string[],
  extras?: PathToGoExtras,
): PathToGoState {
  intent = deriveIntentForSelectedAssessment(intent, site.generationAssessmentSelection);
  const customerId = site.companyId ?? 'unknown';

  // 1. Composite (oracle) + verdict (with the adequacy gate baked in).
  const siteComposite =
    composite ??
    computeSiteComposite({
      site,
      customerId,
      reports,
      evidence: sed,
      bandConfig,
      analysisContext: extras,
    });
  const baseVerdict = deriveSiteVerdict(siteComposite, null, bandConfig ?? undefined);
  const verdict = applyDecisionRollup(baseVerdict, extras?.decisionRollup);

  // Decision stage + the Stage 2 checklist mature validation starts from.
  const decisionStage = resolveSiteDecisionStage(siteComposite);
  const stage2Decisions = stage2DecisionsFor(
    site,
    sed,
    reports,
    decisionStage,
    extras?.asOf ?? new Date().toISOString().slice(0, 10),
  );
  const stepStage = { stage: decisionStage.currentStage };

  const adequacyGate = {
    adequate: verdict.dataAdequate,
    ...(verdict.adequacyReason ? { reason: verdict.adequacyReason } : {}),
    coverageRatio: verdict.coverageRatio ?? 0,
  };

  // 2. Distance to GO — uses the oracle decisionScore. When the verdict is
  //    withheld (no band), distance is reported off the raw composite decision
  //    score but the wizard must surface the adequacy gate first (stages locked).
  const distance = distanceToGo(siteComposite.decisionScore);

  // 3. Required analyses + the canonical lifecycle plan. Generation & Impact
  // applicability is resolved once here and reused by the required-analysis set,
  // Test Fit dependency gate, phase counts, and next-action resolver.
  const powerAvailabilityPreferences =
    site.typedPreferencesDefaults?.['power-availability'];
  const generationImpactApplicability =
    assessGenerationImpactTestFitApplicability({
      projectIntent: intent,
      powerAvailabilityPreferences,
    });
  const requiresGenerationImpact = generationImpactApplicability.required;
  const { requiredTypes: required, scopeTypes, plannedTypes } = resolvePathToGoPlannedTypes(site, intent);
  const present = reportTypesPresent(reports);
  const coordinateFirstBootstrap = isFirstReportForSite(site, reports);
  const preflightByType = new Map<ReportTypeId, {
    runnable: boolean;
    blockingSections: number;
  }>();
  for (const type of plannedTypes) {
    const pf = preflightForType(sed, type, coordinateFirstBootstrap);
    preflightByType.set(type, pf);
  }
  const reportPlan = buildAnalysisPlan({
    site,
    sed,
    reports,
    analysisSnapshots: extras?.analysisSnapshots,
    currentAnalysisInputsByDomain: extras?.currentAnalysisInputsByDomain,
    intent,
    powerAvailabilityPreferences,
    requiredTypes: required,
    scopeTypes,
    preflightByType,
    runtimeByType: extras?.analysisRuntimeByType ?? extras?.reportRuntimeByType,
    outcomes: extras?.analysisOutcomes ?? extras?.reportOutcomes,
    autoRefreshExhaustedByType: extras?.analysisAutoRefreshExhaustedByType,
    analysisGateEnabled: extras?.analysisGateEnabled === true,
    generationTabEnabled: extras?.generationTabEnabled,
    canEdit: extras?.canEdit,
    requiresGenerationImpact,
  });
  const reportsById = new Map(reports.map(report => [report.id, report]));
  const decisionBasisReports = [...new Map(reportPlan.flatMap((item) => {
    if (!(item.contributesToCalculations ?? item.satisfiesGo)) return [];
    const exact = item.calculationReportId
      ? reportsById.get(item.calculationReportId)
      : undefined;
    const selected = exact ?? (
      item.calculationStatus == null
        ? latestUsableSavedReportOfType(reports, item.type)
        : null
    );
    return selected ? [[selected.id, selected] as const] : [];
  })).values()];
  // Compatibility projection for existing consumers. A stale/failed refresh
  // remains visible on its row, but it is not outstanding GO work when a safe
  // saved result already completes the stage.
  const recommendNext: RecommendedReport[] = reportPlan
    .filter((item) => (
      item.role !== 'optional'
      && !planItemCompletesStage(item)
      && !['current', 'queued', 'generating'].includes(item.lifecycle)
    ))
    .map((item) => {
      const pf = preflightByType.get(item.type) ?? {
        runnable: false,
        blockingSections: 0,
      };
      // A Stage 2 analysis waiting on the promoted scenario can read 'ready'
      // while the enqueue chokepoint refuses it (`stageLockFor`) — it is not
      // runnable work until the scenario is promoted.
      const runnable = (item.lifecycle === 'ready' || item.lifecycle === 'refresh-needed')
        && item.eligibility?.reasonCode !== 'stage-locked';
      return {
        type: item.type,
        runnable,
        blocked: !runnable,
        blockingSections: pf.blockingSections,
      };
    });

  // 4. Ranked steps — fuse the gap producers.
  const ranking = rankNextInputs(intent, site, sed);
  const intentSteps = stepsFromIntentRecommendations(ranking.ranked);

  const docMissingRows = intentCriticalMissingRows(
    site,
    sed,
    intent,
    extras?.coverageMatchByType,
    plannedTypes,
  );
  // Detailed design/commercial facts remain in per-report setup. Their absence
  // is not a missing early-screening input or proof of an adverse site fact.
  const missingRows = docMissingRows;
  // Q5 — `intent` only changes the COPY of a decision-fact row whose field was
  // recorded unknown (states the unknown + follow-up); the rows keep firing.
  const coverageSteps = stepsFromCoverageRows(missingRows, intent, stepStage);
  const sedGapSteps = stepsFromSedGaps(sed?.gaps ?? [], intent, stepStage);
  // Open questions the Stage 1 reports raised become evidence requests.
  const screeningQuestionSteps = stepsFromScreeningQuestions(
    stage2Decisions.decisions.flatMap((decision) => decision.openQuestions),
  );
  const readiness = computeEvidenceReadiness(
    site,
    sed,
    [...present].length > 0 ? [...present] : undefined,
  );
  // A readiness recommendation naming an evidence item the coverage layer
  // already reports is the same ask twice on one worklist — coverage wins
  // (see filterReadinessStepsAgainstCoverage).
  const readinessSteps = filterReadinessStepsAgainstCoverage(
    stepsFromReadinessRecommendations(readiness.recommendations),
    missingRows.map((row) => row.label),
  );

  // Preflight steps — blocking SED sections across the recommendation set.
  // A coordinate-only first run has no user-owned source work: public-source
  // collection happens inside the run, and later evidence appears here only
  // when it can improve or unlock a specific refresh.
  const preflightSteps: NextStep[] = [];
  {
    const sections = new Map<string, {
      section: ReturnType<typeof runPreflight>['sections'][number];
      analysisTypes: Set<ReportTypeId>;
    }>();
    for (const type of coordinateFirstBootstrap ? [] : plannedTypes) {
      const required2 = getRequiredSedSectionsFor(type);
      const pf = runPreflight(sed, required2, {
        bootstrapMode: coordinateFirstBootstrap,
        reportTypeId: type,
      });
      for (const s of pf.sections) {
        if (!PREFLIGHT_BLOCKING_STATUSES.has(s.status)) continue;
        const current = sections.get(s.id);
        if (current) {
          current.analysisTypes.add(type);
        } else {
          sections.set(s.id, { section: s, analysisTypes: new Set([type]) });
        }
      }
    }
    for (const { section, analysisTypes } of sections.values()) {
      const [step] = stepsFromPreflight(
        [section],
        PREFLIGHT_BLOCKING_STATUSES as ReadonlySet<string>,
      );
      if (!step) continue;
      preflightSteps.push({
        ...step,
        affectedAnalysisTypes: plannedTypes.filter(type => analysisTypes.has(type)),
        decisionRequired: [...analysisTypes].some(type => required.includes(type)),
      });
    }
  }

  // Site-basis: boundary geometry — the one new REQUIRED-path evidence step
  // (a boundary-less site keeps the evidence stage open until drawn/imported).
  const siteBasisSteps = stepsFromSiteBasisGaps(siteHasBoundary(site));

  const strategyConflict =
    (generationImpactApplicability.btmStrategy === 'grid-only'
      && (
        generationImpactApplicability.hasOnSitePhase
        || generationImpactApplicability.nuclearInterest === 'primary'
        || generationImpactApplicability.nuclearInterest === 'considering'
      ))
    || (
      (generationImpactApplicability.btmStrategy === 'btm-only' || generationImpactApplicability.btmStrategy === 'islanded')
      && generationImpactApplicability.hasGridPhase
    );
  const applicabilityConflictField =
    strategyConflict || !generationImpactApplicability.hasNuclearPhase
      ? 'btmStrategy'
      : 'nuclearInterest';
  const applicabilityStep: NextStep[] =
    generationImpactApplicability.status === 'conflict'
      ? [{
          id: 'intent:generation-strategy-conflict',
          kind: 'intent',
          label: 'Resolve the grid and on-site generation conflict',
          why: generationImpactApplicability.reasons.join(' '),
          severity: 'critical',
          valueScore: 100,
          sourceModule: 'intent-recommender',
          resolver: {
            label: extras?.canEdit === false
              ? 'Request edit access'
              : applicabilityConflictField === 'nuclearInterest'
                ? 'Review nuclear intent'
                : 'Confirm power strategy',
            action: 'open-intent',
            targetId: applicabilityConflictField,
          },
        }]
      : [];
  const requiredTestFitItem = reportPlan.find(
    (item) => item.type === 'test-fit' && item.role === 'required',
  );
  const generationDependencySteps =
    requiredTestFitItem
    && ['dependency-blocked', 'unavailable'].includes(requiredTestFitItem.lifecycle)
      ? stepsFromGenerationGaps({
          blockMessage:
            requiredTestFitItem.blockingReason
          ?? 'Complete Generation & Impact before running the Test Fit analysis.',
          enabled: true,
          required: true,
        })
      : [];

  const rankedSteps = rankSteps([
    ...applicabilityStep,
    ...intentSteps,
    ...coverageSteps,
    ...sedGapSteps,
    ...preflightSteps,
    ...readinessSteps,
    ...siteBasisSteps,
    ...generationDependencySteps,
    ...screeningQuestionSteps,
  ]);

  // OPTIONAL side-quests — separate surface, never in rankedSteps/stage counts.
  const generationEnabled = extras?.generationTabEnabled ?? false;
  const sideQuests: NextStep[] = [
    ...(!requiresGenerationImpact
      ? stepsFromGenerationGaps({
          blockMessage: generationEnabled ? testFitWorkflowBlockMessage(site, sed) : null,
          enabled: generationEnabled,
          required: false,
        })
      : []),
    ...(extras?.simulationTabEnabled === false
      ? []
      : stepsFromAnalysisGaps({
          hasSimulation: (site.simulationIds ?? []).length > 0,
          intent,
        })),
    // Connectivity, Financial and Supply Chain already live in the canonical
    // analysis plan's Optional / Available later lanes. Re-emitting them here
    // would create two competing next actions for the same work.
    ...stepsFromRoadmapGaps(roadmapProgress(extras?.recommendationGraph?.nodes ?? [])),
  ];

  // 5. Blockers — fuse the sources. Thread the live `intent` so the
  //    firm-capacity + BTM-field gates are visible BEFORE a power-availability
  //    report exists, reading the SAME `summarizeIntentFeasibility` oracle the
  //    verdict cap uses (consistency mandate). `verdictDisplay` carries the
  //    firm-capacity cap note + preCapTier so the displayed cap and the path
  //    headline are byte-identical — computed off the live intent via
  //    `capTierByFirmCapacity` (the SAME mechanism `getDisplayVerdict` runs).
  const critDocs = missingCriticalDocs(site, [...new Set<ReportTypeId>([...required, ...scopeTypes])]);
  const verdictDisplay = buildFirmCapacityVerdictDisplay(intent);
  const blockers = buildBlockers({
    generationAssessmentSelection: site.generationAssessmentSelection,
    verdict,
    composite: siteComposite,
    reports: decisionBasisReports,
    missingCriticalDocs: critDocs,
    steps: rankedSteps,
    bandConfig,
    ...(intent ? { intent } : {}),
    ...(verdictDisplay ? { verdictDisplay } : {}),
    ...(bindingConstraints ? { bindingConstraints } : {}),
  });

  // 6. Stages.
  const requiredSatisfied = reportPlan
    .filter((item) => item.role === 'required')
    .every((item) => item.contributesToCalculations ?? item.satisfiesGo);
  const isGo = verdict.dataAdequate && verdict.band === 'go';
  const intentCoverageStatus = site.coverage?.['site-project-intent']?.status ?? null;
  const intentComplete = isIntentDefinitionComplete(ranking, applicabilityStep.length);
  const stages = buildStages({
    adequate: verdict.dataAdequate,
    adequacyReason: verdict.adequacyReason ?? null,
    intentMissingCount: ranking.ranked.length + applicabilityStep.length,
    intentRecordedUnknownCount: ranking.recordedUnknown.length,
    evidenceStepCount: preflightSteps.length + siteBasisSteps.length,
    blockers,
    recommendNext,
    requiredSatisfied,
    isGo,
    intentCoverageStatus,
    intentComplete,
  });
  const defineOpen =
    rankedSteps.filter(
      (step) => step.kind === 'intent' || step.id === 'site-basis:boundary',
    ).length;
  const buildOpen = reportPlan.filter(
    (item) => item.role === 'required' && !planItemCompletesStage(item),
  ).length;
  const requiredTypeSet = new Set(required);
  const verifyOpen = preflightSteps.filter(step => (
    (step.affectedAnalysisTypes ?? []).some(type => requiredTypeSet.has(type))
  )).length + countResolveBlockers(blockers);
  const phases = buildPhases({
    adequate: verdict.dataAdequate,
    adequacyReason: verdict.adequacyReason ?? null,
    defineOpen,
    defineUnknown: ranking.recordedUnknown.length,
    buildOpen,
    verifyOpen,
    requiredSatisfied,
    isGo,
  });
  const firstRequiredAction = reportPlan.find(
    (item) => item.role === 'required' && !planItemCompletesStage(item),
  )?.primaryAction;

  return {
    ...(extras?.decisionRollup ? { decisionRollup: extras.decisionRollup } : {}),
    verdict,
    distanceToGo: distance,
    adequacyGate,
    requiredReports: required,
    requiredAnalyses: required,
    reportPlan,
    analysisPlan: reportPlan,
    recommendNext,
    recommendNextAnalyses: recommendNext,
    rankedSteps,
    blockers,
    stages,
    phases,
    coordinateFirstBootstrap,
    hasScreeningCoordinates: hasScreeningCoordinates(site),
    ...(firstRequiredAction ? { primaryPathAction: firstRequiredAction } : {}),
    generationImpactApplicability,
    sideQuests,
    decisionStage,
    stage2Decisions,
  };
}
